// src/stores/clerkStore.js
import { writable, derived } from 'svelte/store';

/**
 * Clerk Authentication Store
 * Manages authentication state reactively across the app
 */
function createClerkStore() {
  const { subscribe, set, update } = writable({
    user: null,
    isSignedIn: false,
    isLoaded: false,
    error: null
  });

  return {
    subscribe,
    
    /**
     * Initialize store with Clerk instance
     */
    init: (clerk) => {
      if (!clerk) {
        set({
          user: null,
          isSignedIn: false,
          isLoaded: true,
          error: 'Clerk not available'
        });
        return;
      }
      
      // Set initial state
      const initialState = {
        user: clerk.user,
        isSignedIn: !!clerk.user,
        isLoaded: true,
        error: null
      };
      
      set(initialState);
      
      // Listen for auth changes
      clerk.addListener(() => {
        set({
          user: clerk.user,
          isSignedIn: !!clerk.user,
          isLoaded: true,
          error: null
        });
      });
    },
    
    /**
     * Sign in user
     */
    signIn: async () => {
      if (!window.clerk) {
        console.error('Clerk not initialized');
        return;
      }
      
      try {
        await window.clerk.openSignIn();
      } catch (error) {
        console.error('Sign in failed:', error);
        update(state => ({ ...state, error: error.message }));
      }
    },
    
    /**
     * Sign out user
     */
    signOut: async () => {
      if (!window.clerk) return;
      
      try {
        await window.clerk.signOut();
        window.location.href = '/';
      } catch (error) {
        console.error('Sign out failed:', error);
        update(state => ({ ...state, error: error.message }));
      }
    },
    
    /**
     * Clear any errors
     */
    clearError: () => {
      update(state => ({ ...state, error: null }));
    }
  };
}

// Create the store
export const clerkStore = createClerkStore();

// Derived store for user metadata
export const userMetadata = derived(
  clerkStore,
  $clerkStore => {
    if (!$clerkStore.user) return null;
    
    return {
      id: $clerkStore.user.id,
      email: $clerkStore.user.primaryEmailAddress?.emailAddress,
      firstName: $clerkStore.user.firstName,
      lastName: $clerkStore.user.lastName,
      fullName: $clerkStore.user.fullName,
      imageUrl: $clerkStore.user.profileImageUrl,
      role: $clerkStore.user.publicMetadata?.role || 'user'
    };
  }
);